import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Receipt, Download, FileSignature, Calendar } from "lucide-react";

export default function Billing() {
  const [data, setData] = useState({ invoices: [], state: null, contract: null });

  useEffect(() => {
    Promise.all([api.get("/invoices"), api.get("/onboarding/state"), api.get("/contract/me")])
      .then(([i, s, c]) => setData({ invoices: i.data, state: s.data, contract: c.data }));
  }, []);

  const sub = data.state?.subscription;

  return (
    <div className="space-y-8" data-testid="billing-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Receipt className="h-3.5 w-3.5" /> Billing
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Subscription &amp; invoices</h1>
      </header>

      {sub && (
        <section className="glass rounded-2xl p-6" data-testid="billing-current">
          <div className="grid sm:grid-cols-4 gap-4">
            <Stat label="Plan" value={sub.plan_name} />
            <Stat label="Status" value={(sub.status || "—").replace(/_/g, " ")} />
            <Stat label="Approved" value={sub.approved_at ? new Date(sub.approved_at).toLocaleDateString() : "—"} />
            <Stat label="Renewal" value={sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"} />
          </div>
        </section>
      )}

      <section className="glass rounded-2xl p-6">
        <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-indigo-300" /> Invoices
        </h3>
        {data.invoices.length === 0 ? <div className="text-sm text-white/40">No invoices yet.</div> : (
          <table className="w-full text-sm" data-testid="billing-invoices-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/5">
                <th className="py-2 px-2">Invoice</th><th className="py-2 px-2">Plan</th>
                <th className="py-2 px-2">Issue date</th><th className="py-2 px-2">Amount</th>
                <th className="py-2 px-2">Status</th><th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`billing-invoice-${inv.id}`}>
                  <td className="py-3 px-2 font-mono text-indigo-300">{inv.invoice_number}</td>
                  <td className="py-3 px-2">{inv.plan_name}</td>
                  <td className="py-3 px-2 font-mono text-xs text-white/60">{inv.issue_date}</td>
                  <td className="py-3 px-2 font-mono">₹{inv.amount.toLocaleString("en-IN")}</td>
                  <td className="py-3 px-2">
                    <span className={`px-2 py-0.5 rounded font-mono uppercase text-[10px] ${inv.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                      {inv.payment_status}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                      data-testid={`billing-download-${inv.id}`}
                      className="inline-flex items-center gap-1 text-indigo-300 hover:underline text-xs">
                      <Download className="h-3 w-3" /> PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {data.contract && (
        <section className="glass rounded-2xl p-6" data-testid="billing-contract">
          <h3 className="font-heading text-lg font-black mb-3 flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-indigo-300" /> Service Agreement
          </h3>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-mono text-indigo-300">{data.contract.agreement_ref}</span>
            <span className="text-white/40">Signed {new Date(data.contract.accepted_at).toLocaleString()}</span>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contract/${data.contract.id}/pdf`} target="_blank" rel="noreferrer"
              data-testid="billing-contract-pdf"
              className="ml-auto inline-flex items-center gap-1 text-indigo-300 hover:underline text-xs">
              <Download className="h-3 w-3" /> Download PDF
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className="font-heading font-black text-xl mt-1">{value}</div>
    </div>
  );
}
